import { StoryboardSegmentShot } from './types';

// Creator-facing view of a segment's shots: one entry per continuous take
// (clip). The per-cut breakdown stays in the data for the editor — the
// creator just films the clip once, hitting the actions in order, and cuts /
// transitions are added in the edit.
export interface ClipView {
  clipName: string;
  // Sum of the internal cuts' durations.
  durationSec: number | null;
  // Camera setup for the take (from its first cut — cuts within a take share
  // a setup by definition; tighter framings are punch-ins made in the edit).
  framing: string | null;
  shotRole: string | null;
  cameraMovement: string | null;
  // The actions to hit within the clip, in order.
  actions: string[];
  // The suggested lines for the whole clip, joined in speaking order.
  suggested: string | null;
  // Opening title overlay, when this clip carries it.
  title: string | null;
  // The internal edit plan (cut lengths), for team surfaces only.
  cutDurations: (number | null)[];
}

/**
 * Human label for a shot role. "B-Roll" is spelled out (the raw enum reads
 * "broll_product"), and the kind of b-roll goes in parentheses so the leading
 * word stays scannable: Talking head / B-Roll (product).
 */
export function formatShotRole(role: string): string {
  const m = /^broll_(.+)$/.exec(role);
  if (m) return `B-Roll (${m[1].replace(/_/g, ' ')})`;
  return sentenceCase(role);
}

// "close_up" → "Close up". Enum values are snake_case throughout.
function sentenceCase(value: string): string {
  const words = value.replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * The spec chips for a clip, role first (a creator reads "am I talking or
 * filming?" before anything else), then length and camera setup.
 */
export function clipSpecs(clip: ClipView): string[] {
  return [
    clip.shotRole ? formatShotRole(clip.shotRole) : null,
    clip.durationSec != null ? `~${Math.round(clip.durationSec * 10) / 10}s` : null,
    clip.framing ? sentenceCase(clip.framing) : null,
    clip.cameraMovement ? sentenceCase(clip.cameraMovement) : null,
  ].filter((v): v is string => Boolean(v));
}

/**
 * Write edited clip actions back onto the underlying shots. `edits` maps a
 * clipName to its action lines (one per line, in order). Within a clip the
 * i-th line lands on the i-th cut; extra lines join onto the last cut, and
 * cuts with no line left get a null description (the action simply drops off
 * the Do list). Everything else on a shot — duration, framing, spoken line,
 * clip name — is untouched, so downstream mapping and the edit plan survive.
 */
export function applyClipActions(
  shots: StoryboardSegmentShot[],
  edits: Record<string, string[]>,
): StoryboardSegmentShot[] {
  const indicesByClip: Record<string, number[]> = {};
  shots.forEach((shot, i) => {
    const name = shot.clipName;
    if (!name) return;
    (indicesByClip[name] = indicesByClip[name] || []).push(i);
  });
  const out = shots.map(s => ({ ...s }));
  for (const [clipName, actions] of Object.entries(edits)) {
    const indices = indicesByClip[clipName];
    if (!indices) continue;
    indices.forEach((shotIndex, k) => {
      const isLast = k === indices.length - 1;
      const value = isLast ? actions.slice(k).join(' ') : actions[k];
      out[shotIndex].description = value && value.trim() ? value.trim() : null;
    });
  }
  return out;
}

/**
 * Group a segment's shots into clips by clipName. Returns null when the shots
 * predate clip names (old storyboards) — callers fall back to per-cut display.
 */
export function groupShotsIntoClips(shots: StoryboardSegmentShot[] | null | undefined): ClipView[] | null {
  if (!shots || shots.length === 0) return null;
  if (!shots.every(s => s.clipName)) return null;
  const clips: ClipView[] = [];
  for (const shot of shots) {
    const current = clips[clips.length - 1];
    if (!current || current.clipName !== shot.clipName) {
      clips.push({
        clipName: shot.clipName as string,
        durationSec: shot.durationSec,
        framing: shot.framing,
        shotRole: shot.shotRole,
        cameraMovement: shot.cameraMovement,
        actions: shot.description ? [shot.description] : [],
        suggested: shot.line || null,
        title: shot.onScreenText || null,
        cutDurations: [shot.durationSec],
      });
      continue;
    }
    current.durationSec = current.durationSec != null || shot.durationSec != null
      ? (current.durationSec ?? 0) + (shot.durationSec ?? 0)
      : null;
    if (shot.description) current.actions.push(shot.description);
    if (shot.line) current.suggested = current.suggested ? `${current.suggested} ${shot.line}` : shot.line;
    if (shot.onScreenText && !current.title) current.title = shot.onScreenText;
    current.cutDurations.push(shot.durationSec);
  }
  return clips;
}
